/**
 * Extension Tiptap : EmailButton
 *
 * Marque un lien comme "bouton email" — rendu avec fond rouge dans l'éditeur
 * et converti côté serveur en bloc table HTML email-safe.
 *
 * Usage :
 *   editor.commands.insertEmailButton({ href: 'https://...', text: 'Cliquez ici' })
 */

import { Mark, mergeAttributes, type CommandProps } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emailButton: {
      insertEmailButton: (attrs: { href: string; text: string }) => ReturnType;
      unsetEmailButton: () => ReturnType;
    };
  }
}

export const EmailButton = Mark.create({
  name: 'emailButton',

  // Un texte ne peut pas être à la fois lien simple et bouton : les deux
  // marks rendent un <a>, leur superposition produirait des <a> imbriqués
  // que la conversion serveur (convertButtonLinks) ne sait pas traiter.
  excludes: 'link',

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      href: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-email-button]',
        getAttrs: (el) => ({ href: (el as HTMLElement).getAttribute('href') }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-email-button': 'true',
        style:
          'display:inline-block;background-color:#FF0035;color:#ffffff;padding:10px 24px;' +
          'text-decoration:none;border-radius:4px;font-weight:bold;font-size:14px;' +
          'font-family:"Segoe UI",Arial,sans-serif;',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      insertEmailButton:
        ({ href, text }: { href: string; text: string }) =>
        ({ chain }: CommandProps) => {
          return chain()
            .focus()
            .insertContent({
              type: 'text',
              text,
              marks: [{ type: 'emailButton', attrs: { href } }],
            })
            .run();
        },
      unsetEmailButton:
        () =>
        ({ commands }: CommandProps) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
